'use client'

import { useEffect, useState } from 'react'

interface ExitIntentOptions {
  threshold?: number
  maxDisplayCount?: number
  cookieName?: string
  delayBeforeShow?: number
}

export const useExitIntent = (options: ExitIntentOptions = {}) => {
  const {
    threshold = 50,
    maxDisplayCount = 3,
    cookieName = 'exit_intent_shown',
    delayBeforeShow = 5000, // 5 seconds delay before the modal can appear
  } = options

  const [showModal, setShowModal] = useState(false)
  const [canShow, setCanShow] = useState(false)

  useEffect(() => {
    // Wait before allowing the modal to show
    const delayTimer = setTimeout(() => {
      setCanShow(true)
    }, delayBeforeShow)

    // Check if modal has been shown too many times
    const getDisplayCount = () => {
      if (typeof window === 'undefined') return maxDisplayCount
      const count = localStorage.getItem(cookieName)
      return count ? parseInt(count, 10) : 0
    }

    const incrementDisplayCount = () => {
      if (typeof window === 'undefined') return
      const count = getDisplayCount()
      localStorage.setItem(cookieName, String(count + 1))
    }

    const handleMouseLeave = (e: MouseEvent) => {
      // Check if mouse is leaving from the top of the page
      if (
        e.clientY <= threshold &&
        canShow &&
        getDisplayCount() < maxDisplayCount
      ) {
        setShowModal(true)
        incrementDisplayCount()
        // Remove event listener after showing once
        document.removeEventListener('mouseleave', handleMouseLeave)
      }
    }

    // Add event listener only if we can show the modal
    if (canShow && getDisplayCount() < maxDisplayCount) {
      document.addEventListener('mouseleave', handleMouseLeave)
    }

    return () => {
      clearTimeout(delayTimer)
      document.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [threshold, maxDisplayCount, cookieName, delayBeforeShow, canShow])

  const closeModal = () => {
    setShowModal(false)
  }

  return { showModal, closeModal }
}
